/**
 * DID THE TEXT ACTUALLY ARRIVE.
 *
 * On 2026-09-19 the AUTOFIX answer line replied to Chance Pint of Rig
 * Doctor 24/7 and the reply never reached his phone. Everything on our
 * side said it had gone: GoHighLevel answered the POST with a 2xx and a
 * message id (pEDd9wFWQJMWkGCf9fa0), `send()` wrote a comm_events row, and
 * the comms screen showed the shop had answered him. Nothing in this
 * codebase had ever read a message back, so "we sent it" only ever meant
 * "GoHighLevel's API accepted it" -- and there was no way, from inside
 * here, to find out what happened after that.
 *
 * A reply the customer never got is worse than no reply at all, because
 * nothing looks wrong. The lead sits there answered, nobody rings them,
 * and the shop believes it has done its part.
 *
 * ─── GOHIGHLEVEL'S OWN WORDS, NARROWED TO THE ONES SMS USES ────────────
 *
 * `GET /conversations/messages/{id}` carries a `status`, and its documented
 * set spans every channel: connected, delivered, failed, opened, pending,
 * read, scheduled, sent, undelivered, clicked, opt_out. Half of those
 * belong to email and calls.
 *
 * The set below is OURS, deliberately, and it is closed -- the column has a
 * check constraint on exactly these words (migration 0132). A status we do
 * not recognise becomes `unknown` and the platform's own string is kept in
 * `raw.delivery`, so a word GoHighLevel adds next year cannot make the
 * write fail and cost us the reading. That is the same trade
 * `callOutcomeOf` makes in comms-bridge.ts: one vocabulary, written down,
 * with the provider's raw text kept beside it.
 *
 * ─── AND AN ABSENT STATUS IS NOT A STATUS ──────────────────────────────
 *
 * `readDelivery` returns null when the payload says nothing at all. It is
 * the rule the whole feature stands on: a lookup that fails, times out or
 * comes back silent means UNKNOWN, never undelivered. Inventing a failure
 * would put a red flag on a message that arrived perfectly well and send
 * somebody chasing a customer who already has their answer.
 */

/** The only words this system stores. See the check constraint in 0132. */
export type Delivery =
  | "pending" | "scheduled" | "sent"
  | "delivered" | "read"
  | "undelivered" | "failed" | "opt_out"
  | "unknown";

/** Every value the column accepts, in the order a message moves through them. */
export const DELIVERY_STATUSES: readonly Delivery[] = [
  "pending", "scheduled", "sent",
  "delivered", "read",
  "undelivered", "failed", "opt_out",
  "unknown",
] as const;

const KNOWN = new Set<string>(DELIVERY_STATUSES);

/**
 * STILL MOVING, so it is worth asking again.
 *
 * `sent` is in here and that is the whole point of the incident: sent
 * means the carrier took it, not that a phone ever showed it. A message
 * that is still `sent` an hour later is exactly the shape of Chance
 * Pint's.
 *
 * `unknown` is NOT in here. We asked, GoHighLevel answered, and the answer
 * was a word we do not model -- asking again gets the same word and spends
 * the quota to get it.
 */
const IN_FLIGHT = new Set<Delivery>(["pending", "scheduled", "sent"]);

export const stillMoving = (s: Delivery | null): boolean =>
  s === null || IN_FLIGHT.has(s);

/**
 * IT DID NOT GET THERE, and somebody needs to know.
 *
 * `opt_out` sits with the two carrier failures rather than with delivered,
 * because the customer did not receive the message either way. It is not
 * the same fault -- nothing is broken, the person asked us to stop -- so it
 * keeps its own word rather than being folded into `undelivered`.
 */
/**
 * The carrier says this NUMBER cannot take a text: a landline (30006), not
 * in service (30003, 30005) or not a valid number (21211). Nothing is wrong
 * with the line that sent it. Blocks and filtering (30007, 30034) and
 * unknown failures (30008) are not on this list on purpose.
 */
export const cannotTakeTexts = (detail: string | null | undefined): boolean =>
  /\bError (30003|30005|30006|21211)\b/.test(String(detail ?? ""));

export const neverArrived = (s: Delivery | null): boolean =>
  s === "undelivered" || s === "failed" || s === "opt_out";

/**
 * WHAT THE CARRIER SAID, when it said anything.
 *
 * GoHighLevel puts the failure in more than one place depending on how the
 * message was sent: `meta.error` as a {code, type, message} object on a
 * failed send, a bare `error` string at the top level, and `meta.errorCode`
 * on some older shapes. Read all of them rather than picking one, the same
 * way `phoneIn` in ghl-inbound.ts reads a phone out of four payload shapes
 * -- the cost of guessing wrong is a blank reason on the one row anybody
 * will actually open.
 *
 * Truncated, because this lands in a text column that gets rendered on a
 * ledger row, and a carrier can return a paragraph.
 */
function errorIn(m: Record<string, unknown>): string | null {
  const meta = (m["meta"] ?? {}) as Record<string, unknown>;
  const err = (meta["error"] ?? {}) as Record<string, unknown>;

  const code = String(err["code"] ?? meta["errorCode"] ?? "").trim();
  const words = String(err["message"] ?? err["type"] ?? m["error"] ?? "").trim();

  const said = [code, words].filter(Boolean).join(" · ");
  return said ? said.slice(0, 200) : null;
}

/**
 * GoHighLevel's words that mean the same thing as one of ours. `queued` is
 * still on its way out, so it is `pending` and the row is asked about
 * again, rather than `unknown`, which nothing ever asks about twice.
 */
const SAME_AS: Record<string, Delivery> = { queued: "pending" };

/**
 * The message itself, out of whatever the lookup answered.
 *
 * T0052, 2026-10-09. The documented shape of `GET /conversations/messages/
 * {id}` is the message at the top level, and this module was written to
 * it. The live API answers `{ "message": { ...status... }, "traceId" }`,
 * which is how every MechanicRank script that checks a wave reads it
 * (`.message?.status`). So `status` was never at the top level, every
 * lookup read as "said nothing", and from 2026-09-20 to 10-09 not one of
 * 6,505 outbound texts got a delivery status. Both shapes are read, the
 * wrapped one first.
 */
export function messageIn(body: Record<string, unknown>): Record<string, unknown> {
  const inner = body["message"];
  return inner && typeof inner === "object" && !Array.isArray(inner)
    ? inner as Record<string, unknown>
    : body;
}

/**
 * The delivery status of one GoHighLevel message, or null when the payload
 * did not say. Null is "we do not know" and every caller must treat it as
 * such -- see the header. Takes the lookup's whole answer, wrapped or not.
 */
export function readDelivery(body: Record<string, unknown>): {
  status: Delivery;
  /** The carrier's own reason, when there is one. Null otherwise. */
  detail: string | null;
  /** GoHighLevel's word, verbatim, kept whether or not we recognised it. */
  provider: string;
} | null {
  const m = messageIn(body);
  const raw = String(m["status"] ?? "").trim().toLowerCase();
  if (!raw) return null;
  const status: Delivery = KNOWN.has(raw) ? raw as Delivery : SAME_AS[raw] ?? "unknown";
  return { status, detail: errorIn(m), provider: raw };
}

/**
 * HOW A ROW SAYS IT ON THE LEDGER.
 *
 * Three tones rather than a word per status, because the person reading
 * the comms screen is answering one question: did they get it. `null`
 * means say nothing -- an inbound message, or one of the 100,000-odd rows
 * that predate this and never had a status to read.
 *
 * "Sent, not confirmed" is the honest wording for `sent`, and it is the
 * sentence this whole feature exists to be able to write. The old screen
 * said nothing there, which read as delivered.
 */
export function deliveryLabel(
  status: Delivery | null | undefined,
): { text: string; tone: "good" | "wait" | "bad" } | null {
  switch (status) {
    case "delivered": return { text: "Delivered", tone: "good" };
    case "read": return { text: "Read", tone: "good" };
    case "undelivered": return { text: "Not delivered", tone: "bad" };
    case "failed": return { text: "Failed", tone: "bad" };
    case "opt_out": return { text: "Blocked, opted out", tone: "bad" };
    case "pending":
    case "scheduled": return { text: "Sending", tone: "wait" };
    case "sent": return { text: "Sent, not confirmed", tone: "wait" };
    default: return null;                 // unknown, null, or a row from before 0132
  }
}
